import React, { ComponentProps, FC } from 'react';

import styled from 'styled-components';

import ActionButton, {
  ActionButtonVariants,
  IActionButtonProps,
  getTextFromChildren,
} from 'components/action-button';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useStoreContext } from 'state/store';
import noop from 'utils/noop';

import RollsRemaining from '../../../../assets/rolls-remaining';

interface GameplayButtonProps {
  /**
   * is the button a roll button
   *
   * @default false
   **/
  isRollButton?: boolean;
  /**
   * number of rolls remaining
   *
   * @default 0
   **/
  rollsRemaining?: number;
  /**
   * handle the button click
   *
   * @default noop
   **/
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;

  eventName?: CustomEventNames;
  isLoading?: boolean;
  isSmallBtn?: boolean;
  hasRollCount?: boolean;
}

/**
 * This was copied from Action Button.
 *
 * Eventually it should be replaced by a common button component.
 */
const GameplayButtonWhiteVisual = styled.button`
  min-width: 11rem;
  padding: 1rem;
  border: 0;
  border-radius: 9999rem;
  background-color: ${Styles.color.white};
  color: ${Styles.color.primary};
  cursor: pointer;
  font-size: 1.2rem;
  font-family: ${Styles.fontFamily.base};
  font-weight: ${Styles.fontWeight.black};
  letter-spacing: -0.4px;
  user-select: none;

  &[disabled] {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const GameplayWhiteButton: FC<
  IActionButtonProps & ComponentProps<typeof GameplayButtonWhiteVisual>
> = ({ children, eventAttributes, eventType, eventName, onClick, ...props }) => {
  const mParticle = useMParticleContext();
  const { restaurantLogAttributesRef } = useStoreContext();
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      mParticle.trackEvent({
        name: eventName || CustomEventNames.BUTTON_CLICK,
        type: eventType || EventTypes.Navigation,
        attributes: {
          Name: eventName || getTextFromChildren(children),
          ...eventAttributes,
          ...restaurantLogAttributesRef.current,
        },
      });
    } catch (_) {
      // Nothing to do here
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <GameplayButtonWhiteVisual onClick={handleClick} {...props} data-testid="gameplay-button">
      {children}
    </GameplayButtonWhiteVisual>
  );
};

const ButtonInner = styled.div<{ iconSize: string }>`
  display: flex;
  align-items: center;

  > svg {
    height: ${p => p.iconSize} !important;
    width: ${p => p.iconSize} !important;
  }
`;

const ButtonText = styled.div<{ hasRollCount: boolean }>`
  display: flex;
  font-size: 1.2rem;
  /* make up for the inner padding offset */
  ${p => p.hasRollCount && `padding: 0.4rem 1.6rem 0 1.6rem;`}
`;

const SMALL_ICON_SIZE = '2rem';
const REGULAR_ICON_SIZE = '3rem';

// This style override is not following our style guideline but it's ok since it's only for this specific button.
const RollupButton = styled(ActionButton)<{ hasRollCount: boolean }>`
  border: 1px solid ${Styles.color.primary};
  background: #fcb700;
  background: linear-gradient(#ffe500, #fcb700);
  box-shadow: 0px 4px 9px 0px rgba(0, 0, 0, 0.09);
  border: 1px solid #ffe500;
  color: ${Styles.color.black};
  font-weight: ${Styles.fontWeight.heavy};
  ${p => p.hasRollCount && 'padding: 0.3rem 0.5rem;'}

  :active {
    background: #fcb700;
    background: linear-gradient(#fcb700, #ffe500);
  }
  :hover {
    box-shadow: 0px 4px 15px 10px rgba(0, 0, 0, 0.13);
  }
  &[disabled] {
    opacity: 0.4;
    :active {
      background: #fcb700;
      background: linear-gradient(#ffe500, #fcb700);
    }
    :hover {
      background: #fcb700;
      background: linear-gradient(#ffe500, #fcb700);
    }
  }
`;

export const GameplayButton: FC<IActionButtonProps & GameplayButtonProps> = ({
  onClick = noop,
  isRollButton = false,
  rollsRemaining = '-',
  children,
  eventName,
  isSmallBtn,
  hasRollCount = true,
  ...rest
}) => {
  if (isRollButton) {
    const iconSize = isSmallBtn ? SMALL_ICON_SIZE : REGULAR_ICON_SIZE;
    return (
      <RollupButton
        variant={ActionButtonVariants.BOX_SHADOW_PRIMARY}
        {...rest}
        onClick={onClick}
        eventName={eventName}
        hasRollCount={hasRollCount}
      >
        <ButtonInner iconSize={iconSize}>
          {hasRollCount && <RollsRemaining rollsRemaining={rollsRemaining} />}
          <ButtonText hasRollCount={hasRollCount}>{children}</ButtonText>
        </ButtonInner>
      </RollupButton>
    );
  }
  return (
    <ActionButton {...rest} onClick={onClick} eventName={eventName}>
      {children}
    </ActionButton>
  );
};

export default GameplayButton;
